import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, SectionHead } from "@/components/motion";
import { PROJECTS, IMAGES } from "@/data/projects";

const ProjectFeature = ({ p, flip }) => (
  <Reveal y={40}>
    <Link
      to={`/work/${p.slug}`}
      data-testid={`project-card-${p.slug}`}
      className="group grid grid-cols-1 overflow-hidden border border-hairline bg-paper transition-colors duration-500 hover:border-moss/40 lg:grid-cols-12"
    >
      <div className={`relative overflow-hidden lg:col-span-7 ${flip ? "lg:order-2" : ""}`}>
        <img
          src={p.image}
          alt={p.imageAlt}
          loading="lazy"
          className="image-treatment h-[320px] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03] sm:h-[440px] lg:h-full lg:min-h-[540px]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/45 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" aria-hidden="true" />
        <div className="absolute bottom-5 left-5 translate-y-3 border border-paper/30 bg-ink/70 px-4 py-3 opacity-0 backdrop-blur-sm transition-[opacity,transform] duration-500 group-hover:translate-y-0 group-hover:opacity-100">
          <p className="font-serif text-2xl font-light text-champagne">{p.metric.value}</p>
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-paper/70">{p.metric.label}</p>
        </div>
      </div>

      <div className={`flex flex-col justify-between gap-10 p-8 sm:p-12 lg:col-span-5 ${flip ? "lg:order-1" : ""}`}>
        <div>
          <p className="overline-label flex items-center gap-3">
            <span className="font-serif text-2xl font-light normal-case tracking-normal text-champagne">{p.num}</span>
            {p.category}
          </p>
          <p className="mt-5 text-[12px] font-semibold uppercase tracking-[0.14em] text-smoke">{p.cardTitle}</p>
          <h3 className="mt-3 font-serif text-3xl sm:text-4xl font-light leading-[1.12] text-ink">
            {p.home.question}
          </h3>
          <p className="mt-3 text-sm text-fog">{p.home.context}</p>
        </div>
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-fog transition-colors duration-500 group-hover:text-moss">
            {p.home.tags.join(" · ")}
          </p>
          <p
            className="mt-4 border-l-2 border-champagne pl-4 font-serif text-lg italic leading-snug text-smoke transition-[opacity,transform] duration-500 lg:translate-y-2 lg:opacity-0 lg:group-hover:translate-y-0 lg:group-hover:opacity-100"
            data-testid={`project-outcome-${p.slug}`}
          >
            {p.home.outcome}
          </p>
          <span className="mt-6 inline-flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.18em] text-ink">
            {p.home.cta}
            <ArrowRight size={14} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1.5" />
          </span>
        </div>
      </div>
    </Link>
  </Reveal>
);

export default function Work() {
  return (
    <section id="work" className="border-t border-hairline" data-testid="work-section">
      <div className="relative h-[240px] overflow-hidden border-b border-hairline sm:h-[360px]">
        <img
          src={`${IMAGES.lobby}&h=700`}
          alt="Premium hotel lobby — the setting for the case studies below"
          loading="lazy"
          className="image-treatment h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/30 to-transparent" aria-hidden="true" />
      </div>

      <div className="mx-auto max-w-[1400px] px-6 py-24 sm:px-10 sm:py-32">
        <SectionHead
          label="Selected Work"
          title={<>Hospitality problems, <em className="text-moss">examined.</em></>}
          sub="Four questions about how hotels perform, compete and create value."
        />

        <div className="flex flex-col gap-10 sm:gap-16">
          {PROJECTS.map((p, i) => (
            <ProjectFeature key={p.slug} p={p} flip={i % 2 === 1} />
          ))}
        </div>

        <div className="mt-24 border-t border-hairline pt-8 sm:mt-28">
          <Reveal>
            <p className="text-sm leading-relaxed text-fog" data-testid="further-msc-work">
              Further MSc work — revenue management simulation, hospitality information systems, strategic HRM and consumer research — available on request.
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
